// Firebase Auth bootstrap.
//
// We avoid pulling firebase/auth into the bundle until it's actually needed
// (auth mode = 'firebase'), so the mock-mode dev experience stays
// dependency-free at runtime.

import type { Auth, User as FirebaseUser } from 'firebase/auth';

export type AuthMode = 'firebase' | 'mock';

export const authMode: AuthMode =
  (import.meta.env.VITE_AUTH_MODE as AuthMode | undefined) === 'firebase'
    ? 'firebase'
    : 'mock';

let _auth: Auth | null = null;
let _loadPromise: Promise<Auth> | null = null;

/**
 * Lazily initialise Firebase Auth on first use. Throws if VITE_AUTH_MODE is
 * 'firebase' but config env vars are missing — the AuthProvider catches that
 * and surfaces a useful error.
 */
export async function getFirebaseAuth(): Promise<Auth> {
  if (_auth) return _auth;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    const apiKey      = import.meta.env.VITE_FIREBASE_API_KEY;
    const authDomain  = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
    const projectId   = import.meta.env.VITE_FIREBASE_PROJECT_ID;
    const appId       = import.meta.env.VITE_FIREBASE_APP_ID;

    if (!apiKey || !authDomain || !projectId || !appId) {
      throw new Error(
        'Firebase auth selected but VITE_FIREBASE_* env vars are missing. ' +
          'Set them in frontend/.env.local or switch VITE_AUTH_MODE=mock.',
      );
    }

    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getAuth } = await import('firebase/auth');

    const app = getApps().length === 0
      ? initializeApp({
          apiKey,
          authDomain,
          projectId,
          appId,
          measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
        })
      : getApp();

    _auth = getAuth(app);
    return _auth;
  })();

  return _loadPromise;
}

export type { FirebaseUser };
